import { GraphOrganisationAri } from '@atlassian/ari/graph';
import { type PositionTeam, positionTeamsQuery } from '@atlassian/search-client';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { type FetchedSelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/products';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

// Number of results returned in dropdown
const LIMIT = 20;

const mapPositionTeamResult = (
	positionTeam: PositionTeam,
	product: ProductKeys,
): FetchedSelectFilterOption => {
	const id = GraphOrganisationAri.parse(positionTeam.node.ari).organisationUUID;

	return {
		trackingKey: id || '',
		label: positionTeam.node.value?.displayName || '',
		value: id || '',
		queryParamValue: encodeURIComponent(positionTeam.node.ari) || '',
		product: product,
		avatarUrl: positionTeam.node.value?.smallAvatarImageUrl || '',
	};
};

/**
 * Queries position teams by search term
 * @param query
 * @param config
 * @returns
 */
const searchPositionTeams = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `team ~ '${query}'`;
	const positionTeamsResponse = await positionTeamsQuery(config.cloudId || '', LIMIT, rqlQuery);
	const positionTeams: PositionTeam[] = positionTeamsResponse.data?.radar_fieldValues.edges ?? [];

	return positionTeams.map((positionTeam) =>
		mapPositionTeamResult(positionTeam, config.product as ProductKeys),
	);
};

/**
 * Queries all selected position teams by id from query params
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `team in ('${queryParams.map((param) => decodeURIComponent(param)).join("', '")}')`;
	const positionTeamsResponse = await positionTeamsQuery(config.cloudId || '', LIMIT, rqlQuery);
	const positionTeams: PositionTeam[] = positionTeamsResponse.data?.radar_fieldValues.edges ?? [];

	return positionTeams.map((positionTeam) =>
		mapPositionTeamResult(positionTeam, config.product as ProductKeys),
	);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Talent];

export const filter = createMultiSelectFilter({
	id: 'positionTeam',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'positionTeams',
		fetcher: getOptionsWithSelectionsFromQueryParams,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchPositionTeams('', config);
		},
	},
	lookup: {
		fetcher: searchPositionTeams,
	},
});
